import { useJoyfulApp } from './appContext'
import type { JoyfulApiClient } from './api'
import type { paths } from '@joyful/openapi/types.gen'
import { useQueryClient } from '@tanstack/react-query'
import type {
  QueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import type {
  FetchOptions,
  FetchResponse,
  MaybeOptionalInit,
} from 'openapi-fetch'
import type {
  FilterKeys,
  HttpMethod,
  MediaType,
  PathsWithMethod,
  RequiredKeysOf,
} from 'openapi-typescript-helpers'

type InferSelectReturnType<TData, TSelect> = TSelect extends (
  data: TData,
) => infer ReturnValue
  ? ReturnValue
  : TData
type InitWithUnknowns<Init> = Init & {
  [key: string]: unknown
}
type QueryKey<
  PathnameMethod extends HttpMethod,
  Path extends PathsWithMethod<paths, PathnameMethod>,
  Init = MaybeOptionalInit<paths[Path], PathnameMethod>,
> = Init extends undefined
  ? readonly [PathnameMethod, Path]
  : readonly [PathnameMethod, Path, Init]
type TApiQueryKey<Path extends PathsWithMethod<paths, 'get'>> =
  | Path
  | {
      options: FetchOptions<FilterKeys<paths[Path], 'get'>>
      path: Path
    }
type ApiUseMutation = JoyfulApiClient['$api']['useMutation']

export function buildApiQueryKey<Path extends PathsWithMethod<paths, 'get'>>(
  path: Path,
  params?: FetchOptions<FilterKeys<paths[Path], 'get'>>,
) {
  if (params === undefined) {
    return ['get', path] as const
  }

  return ['get', path, params] as const
}

export function buildApiMutationKey<
  PathnameMethod extends HttpMethod,
  Path extends PathsWithMethod<paths, PathnameMethod>,
>(method: PathnameMethod, path: Path) {
  return [method, path] as const
}

/**
 * Use for generated API queries that should share the app-scoped API client.
 *
 * ```typescript
 * const itemsQuery = useApiQuery('get', '/items', {
 *   params: { query: { limit: 50 } },
 * })
 * ```
 */
export function useApiQuery<
  PathnameMethod extends HttpMethod,
  Path extends PathsWithMethod<paths, PathnameMethod>,
  Init extends MaybeOptionalInit<paths[Path], PathnameMethod>,
  Response extends Required<
    FetchResponse<NonNullable<paths[Path][PathnameMethod]>, Init, MediaType>
  >,
  Options extends Omit<
    UseQueryOptions<
      Response['data'],
      Response['error'],
      InferSelectReturnType<Response['data'], Options['select']>,
      QueryKey<PathnameMethod, Path>
    >,
    'queryFn' | 'queryKey'
  >,
>(
  method: PathnameMethod,
  path: Path,
  ...[init, options, queryClient]: RequiredKeysOf<Init> extends never
    ? [InitWithUnknowns<Init>?, Options?, QueryClient?]
    : [InitWithUnknowns<Init>, Options?, QueryClient?]
): UseQueryResult<
  InferSelectReturnType<Response['data'], Options['select']>,
  Response['error']
> {
  const { api } = useJoyfulApp()
  const untypedUseQuery: unknown = api.$api.useQuery
  // SAFETY: openapi-react-query useQuery generics are wider than this wrapper's Path/Init binding.
  const generatedUseQuery = untypedUseQuery as (
    method: PathnameMethod,
    path: Path,
    init: InitWithUnknowns<Init> | undefined,
    options: Options | undefined,
    queryClient: QueryClient | undefined,
  ) => UseQueryResult<
    InferSelectReturnType<Response['data'], Options['select']>,
    Response['error']
  >

  return generatedUseQuery(method, path, init, options, queryClient)
}

/**
 * Use for standard generated API mutations. Use `useApiQueryClient()` alongside
 * it when callbacks need invalidation or other cache utilities.
 *
 * ```typescript
 * const { invalidateApiQueries } = useApiQueryClient()
 * const createMutation = useApiMutation('post', '/items', {
 *   onSuccess: () => {
 *     void invalidateApiQueries('/items')
 *   },
 * })
 * ```
 */
function useApiMutationImplementation(
  method: Parameters<ApiUseMutation>[0],
  path: Parameters<ApiUseMutation>[1],
  options?: Parameters<ApiUseMutation>[2],
) {
  const { api } = useJoyfulApp()
  const untypedUseMutation: unknown = api.$api.useMutation
  const generatedUseMutation = untypedUseMutation as ApiUseMutation

  return generatedUseMutation(method, path, options)
}

const untypedUseApiMutation: unknown = useApiMutationImplementation
// SAFETY: openapi-react-query useMutation call signature is wider than ApiUseMutation.
export const useApiMutation = untypedUseApiMutation as ApiUseMutation

/**
 * Use for cache control and low-level API utilities such as `fetchClient`,
 * `queryClient`, `baseUrl`, and generated query-key helpers.
 *
 * usage:
 *
 * ```typescript
 * const { invalidateApiQueries } = useApiQueryClient();
 *
 * ...
 *
 * invalidateApiQueries(
 *   '/accounts',
 *   {
 *     path: '/users/{user_id}',
 *     options: { params: { path: { user_id: '123' } } },
 *   },
 * );
 * ```
 */
export function useApiQueryClient() {
  const { api } = useJoyfulApp()
  const queryClient = useQueryClient()

  return {
    $api: api.$api,
    authenticatedFetch: api.authenticatedFetch,
    baseUrl: api.baseUrl,
    buildApiMutationKey,
    buildApiQueryKey: <Path extends PathsWithMethod<paths, 'get'>>(
      path: Path,
      params?: FetchOptions<FilterKeys<paths[Path], 'get'>>,
    ) => {
      return params === undefined
        ? buildApiQueryKey(path)
        : api.$api.queryOptions('get', path, params).queryKey
    },
    cancelApiQueries: <
      Keys extends ReadonlyArray<PathsWithMethod<paths, 'get'>>,
    >(
      ...keys: { [Index in keyof Keys]: TApiQueryKey<Keys[Index]> }
    ) => {
      return Promise.all(
        keys.map((key) => {
          return queryClient.cancelQueries({
            queryKey:
              typeof key === 'string'
                ? buildApiQueryKey(key)
                : buildApiQueryKey(key.path, key.options),
          })
        }),
      )
    },
    createAuthenticatedFetch: api.createAuthenticatedFetch,
    fetchClient: api.fetchClient,
    invalidateApiQueries: <
      Keys extends ReadonlyArray<PathsWithMethod<paths, 'get'>>,
    >(
      ...keys: { [Index in keyof Keys]: TApiQueryKey<Keys[Index]> }
    ) => {
      return Promise.all(
        keys.map((key) => {
          return queryClient.invalidateQueries({
            queryKey:
              typeof key === 'string'
                ? buildApiQueryKey(key)
                : buildApiQueryKey(key.path, key.options),
          })
        }),
      )
    },
    queryClient,
  }
}
